import { StandardEvents } from '@shopify/events';
import { createShopPayButton } from '@theme/gsg-shop-pay-sdk';
import { welcomeShopCheckoutUrl } from '@theme/gsg-cart-perks';

class GsgPurchaseBar extends HTMLElement {
  connectedCallback() {
    if (this.parentElement !== document.body) { document.body.append(this); return; }
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.form = this.querySelector('[data-purchase-cart-form]');
    this.selector = this.querySelector('[data-purchase-variant]');
    this.unitSelector = this.querySelector('[data-purchase-unit]');
    this.activeUnit = 0;
    this.unitSelector?.addEventListener('change', () => { this.activeUnit = Number(this.unitSelector.value); this.updateSelection(); }, {signal});
    this.button = this.querySelector('.gsg-purchase__button');
    this.scrollContainer = document.querySelector('.page-wrapper');
    const update = () => this.updateVisibility();
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.desktopQuery.addEventListener('change', update, {signal});
    window.addEventListener('resize', update, {signal,passive:true});
    this.layoutObserver = new ResizeObserver(update);
    const section = this.mainSection();
    if (section) this.layoutObserver.observe(section);
    this.drawerObserver = new MutationObserver(update);
    this.drawerObserver.observe(document.body, {subtree:true,childList:true,attributes:true,attributeFilter:['open']});
    document.addEventListener('theme-drawer:open', update, {signal});
    document.addEventListener('theme-drawer:close', update, {signal});
    document.addEventListener('gsg:overlay-change', update, {signal});
    window.addEventListener('pageshow', () => {
      const bundle = this.bundle();
      if (bundle) bundle.checkoutBusy = false;
      this.button.removeAttribute('aria-busy');
      this.updateSelection();
      update();
    }, {signal});
    window.addEventListener('scroll', update, {signal,passive:true});
    this.scrollContainer?.addEventListener('scroll', update, {signal,passive:true});
    this.selector?.addEventListener('change', () => this.syncMainPicker(), {signal});
    this.mainSection()?.addEventListener('gsg:bundle-change', () => { this.updateSelection(); update(); }, {signal});
    this.form.addEventListener('submit', async event => {
      const bundle = this.bundle();
      if (!bundle?.addToCart) return;
      event.preventDefault();
      this.button.disabled = true;
      this.button.setAttribute('aria-busy','true');
      await bundle.addToCart(this.button);
      this.button.removeAttribute('aria-busy');
      if (!signal.aborted) {
        this.querySelector('[data-purchase-error]').textContent = bundle.errorMsg?.hidden ? '' : bundle.errorMsg?.textContent || '';
        this.updateSelection();
      }
    }, {signal});
    this.mainSection()?.addEventListener(StandardEvents.productSelect, event => {
      if (this.bundle() || event.target.closest('product-card') || !event.promise) return;
      event.promise.then(({detail}) => {
        if (signal.aborted) return;
        const id = detail?.resource?.id;
        if (this.selector) this.selector.value = String(id);
        this.updateSelection();
      });
    }, {signal});
    customElements.whenDefined('buy-box-tiers').then(() => { if (!signal.aborted) this.updateSelection(); });
    update();
    this.updateSelection();
  }
  mainSection() { return document.getElementById(`shopify-section-${this.dataset.sectionId}`); }
  updateVisibility() {
    const section = this.mainSection();
    const controls = section?.querySelector('.gsg-main-purchase-actions') || section?.querySelector('buy-buttons');
    const gallery = section?.querySelector('media-gallery') || section?.querySelector('.product-information__media');
    const header = document.querySelector('#header-group');
    const headerRect = header?.getBoundingClientRect();
    const top = headerRect && headerRect.top <= 0 && headerRect.bottom > 0 ? headerRect.bottom : 0;
    const controlsPassed = !!controls && controls.getBoundingClientRect().bottom <= top;
    const galleryPassed = !this.desktopQuery?.matches || !gallery || gallery.getBoundingClientRect().bottom <= top;
    const overlayOpen = !!document.querySelector('theme-drawer[open], dialog[open], [data-carousel-lightbox]:not([hidden])');
    this.setVisibility(!overlayOpen && controlsPassed && galleryPassed, overlayOpen);
  }
  setVisibility(visible, immediate = false) {
    if (this.requestedVisibility === visible && !immediate) return;
    this.requestedVisibility = visible;
    this.removeEventListener('animationend', this.motionEnd);
    this.classList.remove('gsg-purchase--enter', 'gsg-purchase--exit');
    this.inert = !visible;
    this.setAttribute('aria-hidden', String(!visible));
    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.hidden = !visible;
      return;
    }
    if (!visible && this.hidden) return;
    this.hidden = false;
    this.classList.add(visible ? 'gsg-purchase--enter' : 'gsg-purchase--exit');
    this.motionEnd = event => {
      if (event.target !== this) return;
      if (!this.requestedVisibility) this.hidden = true;
      this.classList.remove('gsg-purchase--enter', 'gsg-purchase--exit');
    };
    this.addEventListener('animationend', this.motionEnd, {once:true});
  }
  bundle() { return this.mainSection()?.querySelector('buy-box-tiers'); }
  syncMainPicker() {
    const option = this.selector?.selectedOptions[0];
    if (!option?.dataset.options) return;
    const values = JSON.parse(option.dataset.options);
    const bundle = this.bundle();
    if (bundle?.getActiveGroups) {
      const group = bundle.getActiveGroups()[this.activeUnit || 0];
      group?.querySelectorAll('[data-option-select]').forEach((select,index) => { select.value = values[index]; });
      if (group) bundle.onOptionChange(group, true);
      return;
    }
    const picker = this.mainSection()?.querySelector('variant-picker');
    let last;
    values.forEach((value,index) => {
      const radio = [...(picker?.querySelectorAll(`input[data-fieldset-index="${index}"]`) || [])].find(input=>input.value===value);
      if (radio) { radio.checked=true; picker.updateSelectedOption(radio); last=radio; }
      else { const select=picker?.querySelectorAll('select')[index]; if(select){select.value=value;last=select;} }
    });
    last?.dispatchEvent(new Event('change',{bubbles:true}));
    this.updateSelection();
  }
  updateSelection() {
    const bundle = this.bundle();
    const selection = bundle?.selection?.();
    if (selection && this.unitSelector) {
      const quantity = selection.quantity;
      this.unitSelector.closest('label').hidden = quantity < 2;
      if (this.unitSelector.options.length !== quantity) {
        this.unitSelector.replaceChildren(...Array.from({length:quantity},(_,index) => new Option(String(index+1),String(index))));
      }
      this.activeUnit = Math.min(this.activeUnit || 0,Math.max(0,quantity-1));
      this.unitSelector.value = String(this.activeUnit);
    }
    const groupId = bundle?.getActiveGroups?.()[this.activeUnit || 0]?.dataset.variantId;
    if (this.selector && (groupId || selection?.id)) this.selector.value = String(groupId || selection.id);
    const option = this.selector?.selectedOptions[0];
    const compare = this.querySelector('[data-purchase-compare]');
    const saving = this.querySelector('[data-purchase-saving]');
    const count = this.querySelector('[data-purchase-quantity]');
    const label = this.querySelector('[data-purchase-option-label]');
    const valid = selection ? selection.available : option?.dataset.available !== 'false';
    if (selection) {
      this.querySelector('[data-purchase-price]').textContent = bundle.money(selection.total);
      compare.textContent = bundle.money(selection.compare);
      const percent = selection.compare > selection.total ? Math.round((selection.compare-selection.total)*100/selection.compare) : 0;
      compare.hidden = !percent;
      saving.hidden = !percent;
      if (percent) saving.textContent = `${this.dataset.saveLabel} ${percent}%`;
      count.textContent = `${selection.quantity} ${this.dataset.itemsLabel} · ${this.dataset.totalLabel}`;
      if (label) label.textContent = selection.quantity>1 ? `${this.dataset.itemLabel} ${this.activeUnit+1}` : this.dataset.optionLabel;
      const shop = this.querySelector('[data-purchase-shop]');
      if (shop) { shop.setAttribute('aria-disabled',String(!valid)); if(valid) shop.href=bundle.shopUrl(selection.items); else shop.removeAttribute('href'); }
    } else if (option) {
      this.querySelector('[data-purchase-price]').textContent = option.dataset.price;
      compare.textContent = option.dataset.compare;
      compare.hidden = !option.dataset.saving;
      saving.hidden = !option.dataset.saving;
      if(option.dataset.saving) saving.textContent = `${this.dataset.saveLabel} ${option.dataset.saving}%`;
    }
    const image = this.querySelector('img');
    const variantImage = bundle?.variants?.find(variant => Number(variant.id) === Number(groupId || selection?.id))?.image || option?.dataset.image;
    if (image && variantImage) { image.src=variantImage; image.removeAttribute('srcset'); }
    this.button.disabled = !valid || bundle?.addBtn?.dataset.busy === 'true';
    this.querySelector('[data-purchase-button-label]').textContent = valid ? this.button.dataset.availableLabel : this.button.dataset.soldOutLabel;
    const items = selection?.items || [{id:Number(this.selector?.value || this.form.elements.namedItem('id')?.value),quantity:Number(this.form.elements.namedItem('quantity')?.value || 1)}];
    this.updateShopButton(items,valid && !this.button.disabled);
  }
  updateShopButton(items, available) {
    const native = this.querySelector('[data-purchase-native-shop]');
    const mixed = this.querySelector('[data-purchase-mixed-shop]');
    if (!native || !mixed) return;
    const uniform = false;
    native.hidden = !uniform;
    native.inert = !available;
    mixed.hidden = uniform;
    if (uniform) {
      const values = {id:String(items[0].id),quantity:String(items[0].quantity)};
      for (const [name,value] of Object.entries(values)) {
        const input = native.elements.namedItem(name);
        if (input && input.value !== value) { input.value=value; input.dispatchEvent(new Event('change',{bubbles:true})); }
      }
    } else {
      const key = JSON.stringify({items,available});
      if (key !== this.shopSelectionKey) {
        mixed.replaceChildren(createShopPayButton({
          variants:items.map(item=>({id:String(item.id),quantity:item.quantity})),
          disabled:!available,
          checkoutUrl:welcomeShopCheckoutUrl(this.dataset.productId),
          source:'online_store',width:'100%',borderRadius:'10px'
        }));
        this.shopSelectionKey=key;
      }
    }
  }
  disconnectedCallback() { this.controller?.abort(); this.drawerObserver?.disconnect(); this.layoutObserver?.disconnect(); this.removeEventListener('animationend',this.motionEnd); }
}
if (!customElements.get('gsg-purchase-bar')) customElements.define('gsg-purchase-bar',GsgPurchaseBar);
